// 系统设置中心(Fluent UI v9 版,从 antd 6 迁移)
//
// 三个 Tab(与 antd 源页顺序一致):备份设置 / 通用设置 / SSO · OAuth
//   - GET /api/v1/admin/settings 读取,PUT /api/v1/admin/settings 整份替换保存
//   - 用 TabList 替代 antd Tabs(状态统一存于一个 RuntimeSettings 对象,切换 Tab 不丢数据)
//   - 用 Label + Input/Switch/Combobox 替代 antd Form.Item
//   - OAuth 用动态 provider 列表替代 antd Form.List
//   - 使用全局 toast(@/lib/toast)替代 antd message toast
import { useState, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  makeStyles,
  tokens,
  Button,
  Input,
  Switch,
  TabList,
  Tab,
  Card,
  CardHeader,
  Combobox,
  Option,
  Label,
  Text,
  Title2,
  Divider,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import {
  DatabaseRegular,
  SettingsRegular,
  ShieldKeyholeRegular,
  AddRegular,
  DeleteRegular,
  ArrowClockwiseRegular,
  SaveRegular,
} from '@fluentui/react-icons';
import { adminApi } from '@/api/admin';
import type {
  RuntimeSettings,
  BackupSettings,
  GeneralSettings,
  OAuthSettings,
  OAuthProviderConfig,
} from '@/api/admin';
import { explainError } from '@/api/client';
import { toast } from '@/lib/toast';
import { resetAuthModeCache, refreshAuthMode } from '@/lib/authMode';
import { HelpPopover } from '@/components/HelpPopover';

// ============================================================
// 默认值 / 工具
// ============================================================
const DEFAULT_SETTINGS: RuntimeSettings = {
  backup: {
    auto_enabled: false,
    auto_schedule: '0 0 3 * * *',
    keep_days: 30,
  },
  general: {
    site_title: 'Ming-GIT',
    allow_registration: true,
    default_visibility: 'private',
    session_ttl_mins: 60,
    max_upload_mb: 16,
    auth_mode: 'local',
    external_host: null,
    external_http_port: null,
    external_https_port: null,
  },
  oauth: {
    enabled: false,
    providers: [],
  },
};

/** 新增 provider 时的默认值(避免空字段触发后端校验失败) */
function newProvider(): OAuthProviderConfig {
  return {
    name: '',
    enabled: true,
    display_name: '',
    base_url: '',
    client_id: '',
    scopes: 'openid profile email',
    flow: 'authorization_code',
    auto_create: true,
    redirect_uri: null,
    discovery_url: null,
    authorization_endpoint: null,
    token_endpoint: null,
    userinfo_endpoint: null,
    icon: null,
    authorize_base_url: null,
  };
}

/** 把服务端返回的 settings 归一化(补齐缺省字段,providers 字段对齐) */
function normalize(s: RuntimeSettings): RuntimeSettings {
  return {
    backup: { ...DEFAULT_SETTINGS.backup, ...s.backup },
    general: { ...DEFAULT_SETTINGS.general, ...s.general },
    oauth: {
      ...DEFAULT_SETTINGS.oauth,
      ...s.oauth,
      providers: (s.oauth?.providers ?? []).map((p) => ({
        ...newProvider(),
        ...p,
      })),
    },
  };
}

// ============================================================
// 样式
// ============================================================
const useStyles = makeStyles({
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    paddingBottom: '32px',
  },
  pageTitle: {
    margin: 0,
  },
  tabsRow: {
    marginTop: '4px',
  },
  tabPanel: {
    marginTop: '8px',
  },
  panelBody: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '16px',
  },
  fieldLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  fieldRequired: {
    color: tokens.colorStatusDangerForeground1,
    marginLeft: '2px',
  },
  fieldHint: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  fieldError: {
    fontSize: '12px',
    color: tokens.colorStatusDangerForeground1,
  },
  inlineRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  switchItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  numberWithSuffix: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  suffix: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  saveBar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    marginTop: '8px',
  },
  sectionHeader: {
    fontSize: '15px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    margin: '4px 0',
  },
  providerSectionTitle: {
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '0.02em',
    color: tokens.colorBrandForeground1,
    textTransform: 'uppercase',
    marginTop: '8px',
  },
  providerSectionHint: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    marginTop: '2px',
    marginBottom: '4px',
  },
  providerCard: {
    marginBottom: '16px',
  },
  providerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },
  providerTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  addProviderRow: {
    display: 'flex',
    justifyContent: 'center',
  },
  providerIcon: {
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    objectFit: 'contain',
    flexShrink: 0,
  },
  iconPreview: {
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    objectFit: 'contain',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  loadingCenter: {
    display: 'flex',
    justifyContent: 'center',
    padding: '48px',
  },
  dangerButton: {
    color: tokens.colorStatusDangerForeground1,
    ':hover': {
      backgroundColor: tokens.colorStatusDangerBackground1,
    },
  },
});

// ============================================================
// Field —— 统一的 Label + 控件 + 校验消息行(替代 antd Form.Item)
// ============================================================
function Field(props: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  help?: string;
  children: ReactNode;
}) {
  const { label, htmlFor, required, hint, error, help, children } = props;
  const styles = useStyles();
  return (
    <div className={styles.field}>
      <Label htmlFor={htmlFor} className={styles.fieldLabel}>
        {label}
        {required && <span className={styles.fieldRequired}>*</span>}
        {help && <HelpPopover content={help} />}
      </Label>
      {children}
      {error ? (
        <Text className={styles.fieldError}>{error}</Text>
      ) : hint ? (
        <Text className={styles.fieldHint}>{hint}</Text>
      ) : null}
    </div>
  );
}

// ============================================================
// SettingsHubPage
// ============================================================
export function SettingsHubPage() {
  const styles = useStyles();
  const [settings, setSettings] = useState<RuntimeSettings>(DEFAULT_SETTINGS);
  const [server, setServer] = useState<RuntimeSettings | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('backup');

  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['settings'],
    queryFn: () => adminApi.getSettings(),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  // 查询成功后同步状态
  useEffect(() => {
    if (!data) return;
    const next = normalize(data);
    setSettings(next);
    setServer(next);
    setErrors({});
  }, [data]);

  if (isLoading && !server) {
    return (
      <div className={styles.loadingCenter}>
        <Spinner label="加载设置中…" />
      </div>
    );
  }
  if (error && !server) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{explainError(error)}</MessageBarBody>
      </MessageBar>
    );
  }

  // ── 通用 patch 辅助 ──
  const patchBackup = (patch: Partial<BackupSettings>) =>
    setSettings((s) => ({ ...s, backup: { ...s.backup, ...patch } }));
  const patchGeneral = (patch: Partial<GeneralSettings>) =>
    setSettings((s) => ({ ...s, general: { ...s.general, ...patch } }));
  const patchOAuth = (patch: Partial<OAuthSettings>) =>
    setSettings((s) => ({ ...s, oauth: { ...s.oauth, ...patch } }));
  const patchProvider = (idx: number, patch: Partial<OAuthProviderConfig>) =>
    setSettings((s) => ({
      ...s,
      oauth: {
        ...s.oauth,
        providers: s.oauth.providers.map((p, i) =>
          i === idx ? { ...p, ...patch } : p,
        ),
      },
    }));
  const addProvider = () =>
    setSettings((s) => ({
      ...s,
      oauth: {
        ...s.oauth,
        providers: [...s.oauth.providers, newProvider()],
      },
    }));
  const removeProvider = (idx: number) =>
    setSettings((s) => ({
      ...s,
      oauth: {
        ...s.oauth,
        providers: s.oauth.providers.filter((_, i) => i !== idx),
      },
    }));

  // 数值字段解析(替代 antd InputNumber)
  const parseNum = (raw: string): number => {
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? 0 : n;
  };

  // ── 校验(对应 antd Form rules) ──
  function validate(): boolean {
    const e: Record<string, string> = {};
    const b = settings.backup;
    if (!b.auto_schedule.trim()) e['backup.auto_schedule'] = '必填';
    if (!b.keep_days || b.keep_days < 1 || b.keep_days > 365)
      e['backup.keep_days'] = '取值范围 1–365';

    const g = settings.general;
    if (!g.site_title.trim()) e['general.site_title'] = '必填';
    else if (g.site_title.length > 80) e['general.site_title'] = '最多 80 字符';
    if (!g.session_ttl_mins || g.session_ttl_mins < 1 || g.session_ttl_mins > 10080)
      e['general.session_ttl_mins'] = '取值范围 1–10080';
    if (!g.max_upload_mb || g.max_upload_mb < 1 || g.max_upload_mb > 1024)
      e['general.max_upload_mb'] = '取值范围 1–1024';
    if (g.auth_mode && g.auth_mode !== 'local' && g.auth_mode !== 'oauth_only')
      e['general.auth_mode'] = '仅支持 local / oauth_only';

    settings.oauth.providers.forEach((p, idx) => {
      if (!p.name.trim()) e[`p${idx}.name`] = '必填,用于路由 / 绑定';
      if (!p.display_name.trim()) e[`p${idx}.display_name`] = '必填';
      if (!p.base_url.trim()) e[`p${idx}.base_url`] = '必填,不含末尾斜杠';
      if (!p.client_id.trim()) e[`p${idx}.client_id`] = '必填';
      if (!p.scopes.trim()) e[`p${idx}.scopes`] = '必填';
      if (p.icon && !/^(https?:\/\/|data:)/.test(p.icon.trim()))
        e[`p${idx}.icon`] = '需为 http(s) 或 data: URI';
      if (p.authorize_base_url && !/^https?:\/\//.test(p.authorize_base_url.trim()))
        e[`p${idx}.authorize_base_url`] = '需为 http(s) 地址';
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSave() {
    if (!validate()) {
      toast.error('请修正标红的字段后再保存');
      return;
    }
    setSaving(true);
    try {
      const updated = await adminApi.updateSettings(settings);
      const next = normalize(updated);
      setSettings(next);
      setServer(next);
      setErrors({});
      // 写回查询缓存,避免 brief 之后重新进入页面时回显旧值
      queryClient.setQueryData<RuntimeSettings>(['settings'], next);
      // 鉴权模式改动后,前端缓存要立即刷新,否则 LoginPage 仍按旧 mode 渲染。
      // 强制清缓存让下一次 getAuthMode() 重新拉后端 /auth/mode。
      resetAuthModeCache();
      void refreshAuthMode();
      toast.success('设置已保存');
    } catch (e) {
      toast.error(explainError(e));
    } finally {
      setSaving(false);
    }
  }

  function onReset() {
    const next = server ?? DEFAULT_SETTINGS;
    setSettings(next);
    setErrors({});
    toast.info('已重置为服务端值');
  }

  return (
    <div className={styles.container}>
      <Title2 as="h1" className={styles.pageTitle}>
        系统设置
      </Title2>

      <TabList
        selectedValue={activeTab}
        onTabSelect={(_, d) => setActiveTab(d.value as string)}
        className={styles.tabsRow}
      >
        <Tab value="backup">
          <DatabaseRegular /> 备份设置
        </Tab>
        <Tab value="general">
          <SettingsRegular /> 通用设置
        </Tab>
        <Tab value="oauth">
          <ShieldKeyholeRegular /> OAuth
        </Tab>
      </TabList>

      <div className={styles.tabPanel}>
        {activeTab === 'backup' && (
          <BackupPanel
            settings={settings.backup}
            errors={errors}
            onPatch={patchBackup}
            parseNum={parseNum}
          />
        )}
        {activeTab === 'general' && (
          <GeneralPanel
            settings={settings.general}
            errors={errors}
            onPatch={patchGeneral}
            parseNum={parseNum}
          />
        )}
        {activeTab === 'oauth' && (
          <OAuthPanel
            settings={settings.oauth}
            authMode={settings.general.auth_mode}
            onAuthModeChange={(v) => patchGeneral({ auth_mode: v })}
            errors={errors}
            onPatch={patchOAuth}
            onPatchProvider={patchProvider}
            onAdd={addProvider}
            onRemove={removeProvider}
          />
        )}
      </div>

      <Divider />
      <div className={styles.saveBar}>
        <Button
          appearance="primary"
          icon={<SaveRegular />}
          disabled={saving}
          onClick={onSave}
        >
          {saving ? '保存中…' : '保存设置'}
        </Button>
        <Button
          appearance="outline"
          icon={<ArrowClockwiseRegular />}
          disabled={saving}
          onClick={onReset}
        >
          重置
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// BackupPanel — 备份设置
// ============================================================
function BackupPanel(props: {
  settings: BackupSettings;
  errors: Record<string, string>;
  onPatch: (patch: Partial<BackupSettings>) => void;
  parseNum: (raw: string) => number;
}) {
  const { settings: b, errors, onPatch, parseNum } = props;
  const styles = useStyles();
  return (
    <Card size="small">
      <div className={styles.panelBody}>
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>自动备份</MessageBarTitle>
            按计划把 Git 仓库(源快照 + 全量历史)与 SQLite 元数据库分别打包成 .zip。保留策略按「保留天数」清理旧备份。
          </MessageBarBody>
        </MessageBar>

        <Field
          label="启用自动备份"
          hint="关闭后即使下面的 cron 配置正确也不会执行"
          help="总开关。关闭后即使下面的 Cron 计划配置正确,也不会执行任何自动备份。"
        >
          <Switch
            checked={b.auto_enabled}
            onChange={(_, d) => onPatch({ auto_enabled: d.checked })}
          />
        </Field>

        <Field
          label="Cron 计划"
          required
          error={errors['backup.auto_schedule']}
          hint="6 字段:秒 分 时 日 月 周。例:0 0 3 * * * = 每天 03:00。修改后下一次触发即按新计划。"
          help="6 字段的 cron 表达式:秒 分 时 日 月 周。例如 0 0 3 * * * 表示每天 03:00 执行。保存后下一次触发即按新计划。"
        >
          <Input
            value={b.auto_schedule}
            onChange={(_, d) => onPatch({ auto_schedule: d.value })}
            placeholder="0 0 3 * * *"
          />
        </Field>

        <Field
          label="保留天数"
          required
          error={errors['backup.keep_days']}
          hint="仅保留最近 N 天的备份(1–365)。"
          help="仅保留最近 N 天的备份文件(取值 1–365),更早的备份会被自动清理以节省空间。"
        >
          <div className={styles.numberWithSuffix}>
            <Input
              type="number"
              value={String(b.keep_days)}
              onChange={(_, d) => onPatch({ keep_days: parseNum(d.value) })}
              style={{ maxWidth: 200 }}
            />
            <span className={styles.suffix}>天</span>
          </div>
        </Field>
      </div>
    </Card>
  );
}

// ============================================================
// GeneralPanel — 通用设置
// ============================================================
function GeneralPanel(props: {
  settings: GeneralSettings;
  errors: Record<string, string>;
  onPatch: (patch: Partial<GeneralSettings>) => void;
  parseNum: (raw: string) => number;
}) {
  const { settings: g, errors, onPatch, parseNum } = props;
  const styles = useStyles();
  return (
    <Card size="small">
      <div className={styles.panelBody}>
        <Field
          label="站点名称"
          required
          error={errors['general.site_title']}
          help="站点在浏览器标题、通知邮件等处的显示名称,最多 80 字符。"
        >
          <Input
            value={g.site_title}
            onChange={(_, d) => onPatch({ site_title: d.value })}
            maxLength={80}
          />
        </Field>

        <Field
          label="开放注册"
          hint="关闭后,登录页与顶栏的「注册」入口会被隐藏,且任何人无法自助注册。"
          help="总开关。关闭后登录页与顶栏的「注册」入口会被隐藏,且禁止任何人自助注册,仅管理员可在「用户管理」中创建账号。"
        >
          <Switch
            checked={g.allow_registration}
            onChange={(_, d) => onPatch({ allow_registration: d.checked })}
          />
        </Field>

        <Field
          label="新仓库默认可见性"
          hint="创建仓库时未显式选择可见性时使用此默认值。"
          help="创建仓库时若未显式选择可见性,则采用此默认值(私有或公开)。"
        >
          <Combobox
            selectedOptions={[g.default_visibility]}
            onOptionSelect={(_, d) => {
              const v = d.selectedOptions[0] as
                | GeneralSettings['default_visibility']
                | undefined;
              if (v) onPatch({ default_visibility: v });
            }}
            style={{ maxWidth: 220 }}
          >
            <Option value="private">私有 (private)</Option>
            <Option value="public">公开 (public)</Option>
          </Combobox>
        </Field>

        <Field
          label="登录会话时长"
          required
          error={errors['general.session_ttl_mins']}
          hint="access token 有效期(1–10080,即 7 天)。重启生效。"
          help="登录后 access token 的有效期(分钟),范围 1–10080(即 7 天)。修改后需重启服务生效。"
        >
          <div className={styles.numberWithSuffix}>
            <Input
              type="number"
              value={String(g.session_ttl_mins)}
              onChange={(_, d) =>
                onPatch({ session_ttl_mins: parseNum(d.value) })
              }
              style={{ maxWidth: 200 }}
            />
            <span className={styles.suffix}>分钟</span>
          </div>
        </Field>

        <Field
          label="最大上传体积"
          required
          error={errors['general.max_upload_mb']}
          hint="影响请求体大小上限(1–1024)。重启生效。"
          help="HTTP 请求体大小上限(MB),范围 1–1024。限制仓库推送 / 头像上传等请求的最大体积。修改后需重启服务生效。"
        >
          <div className={styles.numberWithSuffix}>
            <Input
              type="number"
              value={String(g.max_upload_mb)}
              onChange={(_, d) => onPatch({ max_upload_mb: parseNum(d.value) })}
              style={{ maxWidth: 200 }}
            />
            <span className={styles.suffix}>MB</span>
          </div>
        </Field>

        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>对外仓库地址</MessageBarTitle>
            gitman 作为独立产品,clone 应直连 gitman 自身的域名/IP 与端口,不经过外部 API 网关。以下三项用于拼装仓库的 clone URL;留空时分别回落为「浏览器访问到的 hostname」与「服务端 config 里的对外端口」。
          </MessageBarBody>
        </MessageBar>

        <Field
          label="对外仓库域名 / IP"
          error={errors['general.external_host']}
          hint="仅裸域名或 IP,不含协议、端口与路径。留空则用浏览器访问到的 hostname 兜底。"
          help="示例:git.example.com 或 127.0.0.1。不要填 http:// 或端口。配置后 clone 地址将直连这里指向的 gitman 实例。"
        >
          <Input
            value={g.external_host ?? ''}
            placeholder="git.example.com 或 127.0.0.1(留空自动兜底)"
            onChange={(_, d) => {
              const v = d.value.trim();
              onPatch({ external_host: v || null });
            }}
          />
        </Field>

        <Field
          label="对外 HTTP 端口"
          error={errors['general.external_http_port']}
          hint="默认 80。容器/NAT 部署时填映射后的对外端口(如 18081);留空回落服务端 config。"
          help="该端口用于拼写 HTTP clone 地址。若 git HTTP(Smart)服务对外映射在 8080/18081 等端口,请在此填写,避免 clone 走到其他占用该端口的服务。"
        >
          <Input
            type="number"
            value={g.external_http_port == null ? '' : String(g.external_http_port)}
            placeholder="如 18081(留空自动兜底)"
            onChange={(_, d) => {
              const n = parseNum(d.value);
              onPatch({ external_http_port: n > 0 ? n : null });
            }}
            style={{ maxWidth: 200 }}
          />
        </Field>

        <Field
          label="对外 HTTPS 端口"
          error={errors['general.external_https_port']}
          hint="默认 443。仅 HTTPS 启用时生效;留空回落服务端 config。"
          help="该端口用于拼写 HTTPS clone 地址。仅当你为 gitman 配置了 TLS 且对外通过该端口暴露时填写。"
        >
          <Input
            type="number"
            value={g.external_https_port == null ? '' : String(g.external_https_port)}
            placeholder="如 18443(留空自动兜底)"
            onChange={(_, d) => {
              const n = parseNum(d.value);
              onPatch({ external_https_port: n > 0 ? n : null });
            }}
            style={{ maxWidth: 200 }}
          />
        </Field>
      </div>
    </Card>
  );
}

// ============================================================
// OAuthPanel — OAuth
// ============================================================
function OAuthPanel(props: {
  settings: OAuthSettings;
  authMode: string;
  onAuthModeChange: (v: 'local' | 'oauth_only') => void;
  errors: Record<string, string>;
  onPatch: (patch: Partial<OAuthSettings>) => void;
  onPatchProvider: (idx: number, patch: Partial<OAuthProviderConfig>) => void;
  onAdd: () => void;
  onRemove: (idx: number) => void;
}) {
  const {
    settings: o,
    authMode,
    onAuthModeChange,
    errors,
    onPatch,
    onPatchProvider,
    onAdd,
    onRemove,
  } = props;
  const styles = useStyles();
  return (
    <Card size="small">
      <div className={styles.panelBody}>
        <MessageBar intent="info">
          <MessageBarBody>
            <MessageBarTitle>OAuth 2.0 / OIDC</MessageBarTitle>
            对接外部 OAuth2 / OIDC 提供方(如 ming-oauth2)。用户用 IdP 凭证登录后,gitman 自动映射或创建本地账号并签发自己的 JWT。client_secret 不在本页配置(由服务器 config 注入,更安全)。
          </MessageBarBody>
        </MessageBar>

        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
          <Field
            label="允许本地登录"
            hint="关闭后登录页隐藏本地账号表单,仅保留 OAuth 入口。"
            help="关闭后,登录页将隐藏本地账号表单,后端也会独立拦截 POST /auth/login 与 /auth/register 返回 403。若同时关闭「启用 OAuth 登录」将无任何登录途径,重启服务会自动重新开启本地登录以防锁死。"
          >
            <Switch
              checked={authMode !== 'oauth_only'}
              onChange={(_, d) =>
                onAuthModeChange(d.checked ? 'local' : 'oauth_only')
              }
            />
          </Field>

          <Field
            label="启用 OAuth 登录"
            hint="总开关。关闭后即使配置了 provider,登录页也不会出现 OAuth 入口。"
            help="总开关。关闭后即使已配置 provider,登录页也不会显示 OAuth 登录入口。"
          >
            <Switch
              checked={o.enabled}
              onChange={(_, d) => onPatch({ enabled: d.checked })}
            />
          </Field>
        </div>

        <Divider />
        <Text className={styles.sectionHeader}>身份提供方(Providers)</Text>

        {o.providers.length === 0 && (
          <MessageBar intent="info">
            <MessageBarBody>
              还没有 provider,点击下方「添加提供方」开始配置。
            </MessageBarBody>
          </MessageBar>
        )}

        {o.providers.map((p, idx) => (
          <ProviderCard
            key={idx}
            index={idx}
            provider={p}
            errors={errors}
            onPatch={onPatchProvider}
            onRemove={onRemove}
          />
        ))}

        <div className={styles.addProviderRow}>
          <Button
            appearance="outline"
            icon={<AddRegular />}
            onClick={onAdd}
          >
            添加提供方
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ============================================================
// ProviderCard — 单个 OAuth 2.0 / OIDC provider 配置卡
//   仅支持标准授权码 + PKCE 流程(authorization_code):
//   base_url / client_id / scopes + Discovery / 显式端点 + 回调地址 + 开关。
// ============================================================
function ProviderCard(props: {
  index: number;
  provider: OAuthProviderConfig;
  errors: Record<string, string>;
  onPatch: (idx: number, patch: Partial<OAuthProviderConfig>) => void;
  onRemove: (idx: number) => void;
}) {
  const { index, provider: p, errors, onPatch, onRemove } = props;
  const styles = useStyles();
  const [showExplicit, setShowExplicit] = useState(false);
  const title = p.display_name || p.name || `提供方 #${index + 1}`;
  const err = (k: string) => errors[`p${index}.${k}`];
  const patch = (patch: Partial<OAuthProviderConfig>) => onPatch(index, patch);

  return (
    <Card size="small" className={styles.providerCard}>
      <CardHeader
        header={
          <span className={styles.providerHeader}>
            {p.icon ? (
              <img src={p.icon} alt="" className={styles.providerIcon} />
            ) : (
              <ShieldKeyholeRegular />
            )}
            <span className={styles.providerTitle}>{title}</span>
          </span>
        }
        action={
          <Button
            size="small"
            appearance="subtle"
            className={styles.dangerButton}
            icon={<DeleteRegular />}
            onClick={() => onRemove(index)}
          >
            删除
          </Button>
        }
      />
      <div className={styles.panelBody}>
        {/* ① 基础信息(OAuth 2.0 / OIDC 通用) */}
        <Text className={styles.providerSectionTitle}>基础信息</Text>
        <Text className={styles.providerSectionHint}>OAuth 2.0 / OIDC 通用字段。</Text>

        <Field label="唯一标识 (name)" required error={err('name')} help="provider 的路由标识,用于 /api/v1/auth/oauth/{name}/... 等路径。需唯一,创建后不可修改。">
          <Input value={p.name} onChange={(_, d) => patch({ name: d.value })} placeholder="ming" />
        </Field>

        <Field label="登录按钮文案" required error={err('display_name')} help="登录页 OAuth 按钮上显示的文字,例如「使用 Ming 账号登录」。">
          <Input value={p.display_name} onChange={(_, d) => patch({ display_name: d.value })} placeholder="使用 Ming 账号登录" />
        </Field>

        <Field
          label="图标 URL(可选)"
          error={err('icon')}
          hint="登录按钮与列表展示用,支持 http(s) 链接或 data: URI。留空则用默认图标。"
          help="登录按钮与列表展示用的图标,支持 http(s) 链接或 data: URI。留空则使用默认图标。"
        >
          <Input value={p.icon ?? ''} onChange={(_, d) => patch({ icon: d.value || null })} placeholder="https://idp.example.com/logo.svg" />
        </Field>
        {p.icon && (
          <img src={p.icon} alt="图标预览" className={styles.iconPreview} />
        )}

        <Field
          label="IdP 基址 (base_url)"
          required
          error={err('base_url')}
          hint="后端用于换取 token / 拉取 userinfo 的地址(容器网络可达,如 http://ming-gateway:8080)。不含末尾斜杠。"
          help="后端用于换取 token / 拉取 userinfo 的地址,需容器网络可达(如 http://ming-gateway:8080),不含末尾斜杠。"
        >
          <Input value={p.base_url} onChange={(_, d) => patch({ base_url: d.value })} placeholder="https://hub.example.com" />
        </Field>

        <Field label="client_id" required error={err('client_id')} help="在 IdP 注册应用时获得的客户端 ID,用于标识本应用。">
          <Input value={p.client_id} onChange={(_, d) => patch({ client_id: d.value })} placeholder="gitman-web" />
        </Field>

        <Field
          label="scope"
          required
          error={err('scopes')}
          hint="需含 openid profile email 才能拿到用户名 / 邮箱 / 角色。"
          help="请求的授权范围,需含 openid profile email 才能拿到用户名 / 邮箱 / 角色等信息。"
        >
          <Input value={p.scopes} onChange={(_, d) => patch({ scopes: d.value })} placeholder="openid profile email" />
        </Field>

        <Divider />

        {/* ③ OAuth 2.0 / OIDC 端点 */}
        <>
          <Text className={styles.providerSectionTitle}>OAuth 2.0 / OIDC 端点</Text>
            <Text className={styles.providerSectionHint}>
              标准授权码流程:浏览器跳转 IdP 登录页,gitman 后端用 code 换 token。
            </Text>

            <Field
              label="浏览器跳转地址 (authorize_base_url,可选)"
              error={err('authorize_base_url')}
              hint="浏览器被重定向到的 IdP 地址(需宿主机可达,如 http://localhost:8080)。默认与 base_url 相同;当浏览器与 gitman 后端不在同一网络(容器部署常见)时,填成浏览器能访问的地址,否则授权跳转打不到 IdP。"
              help="浏览器被重定向到的 IdP 授权地址(需宿主机可达,如 http://localhost:8080)。容器部署时浏览器与后端不在同一网络,需填成浏览器能访问的地址,否则授权跳转无法到达 IdP。留空则与 base_url 相同。"
            >
              <Input
                value={p.authorize_base_url ?? ''}
                onChange={(_, d) => patch({ authorize_base_url: d.value || null })}
                placeholder="http://localhost:8080(留空=与 base_url 相同)"
              />
            </Field>

            <Field
              label="Discovery 文档地址 (可选,推荐)"
              hint="填 OIDC Discovery 文档(如 https://idp/.well-known/openid-configuration)即可自动发现各端点,仅需 issuer + client_id。ming-oauth2 暂无 Discovery 文档,请用下方显式端点。"
              help="OIDC Discovery 文档 URL(如 https://idp/.well-known/openid-configuration),填了可自动发现 authorize / token / userinfo 各端点,通常只需 issuer + client_id。ming-oauth2 暂无 Discovery 文档,请用下方显式端点。"
            >
              <Input
                value={p.discovery_url ?? ''}
                onChange={(_, d) => patch({ discovery_url: d.value || null })}
                placeholder="https://idp.example.com/.well-known/openid-configuration"
              />
            </Field>

            <div className={styles.switchItem}>
              <Switch
                checked={showExplicit}
                onChange={(_, d) => setShowExplicit(d.checked)}
              />
              <Text>使用显式端点(无 Discovery 时填)</Text>
            </div>

            {showExplicit && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Input
                  value={p.authorization_endpoint ?? ''}
                  onChange={(_, d) =>
                    patch({ authorization_endpoint: d.value || null })
                  }
                  placeholder="authorization_endpoint"
                />
                <Input
                  value={p.token_endpoint ?? ''}
                  onChange={(_, d) => patch({ token_endpoint: d.value || null })}
                  placeholder="token_endpoint"
                />
                <Input
                  value={p.userinfo_endpoint ?? ''}
                  onChange={(_, d) =>
                    patch({ userinfo_endpoint: d.value || null })
                  }
                  placeholder="userinfo_endpoint"
                />
              </div>
            )}
          </>

        <Divider />

        {/* ④ 回调与开关 */}
        <Text className={styles.providerSectionTitle}>回调与开关</Text>

        <Field label="回调地址 (可选)" help="gitman 的 OAuth 回调地址,需与 IdP 中注册的回调一致。留空则自动从请求推导。">
          <Input
            value={p.redirect_uri ?? ''}
            onChange={(_, d) => patch({ redirect_uri: d.value || null })}
            placeholder="留空则自动推导(需与 IdP 注册一致)"
          />
        </Field>

        <div className={styles.inlineRow}>
          <div className={styles.switchItem}>
            <Switch
              checked={p.enabled}
              onChange={(_, d) => patch({ enabled: d.checked })}
            />
            <Text>
              启用 <HelpPopover content="是否启用该 provider。关闭后登录页不会显示此 OAuth 入口,其它配置保留。" />
            </Text>
          </div>
          <div className={styles.switchItem}>
            <Switch
              checked={p.auto_create}
              onChange={(_, d) => patch({ auto_create: d.checked })}
            />
            <Text>
              自动创建账号 <HelpPopover content="开启后,首次用该 IdP 登录的未知用户会自动创建本地账号并签发 JWT;关闭则仅允许已存在(已绑定)的用户登录。" />
            </Text>
          </div>
        </div>
      </div>
    </Card>
  );
}
